import { useState } from "react";
import { 
  Stethoscope, AlertTriangle, Users, DollarSign, FileText, 
  Shield, Loader2, CheckCircle2, XCircle, ChevronRight, 
  Building, Phone, Scale, Search, Network, Receipt,
  AlertOctagon, TrendingUp, HelpCircle
} from "lucide-react";
import { VerificationPanel, VerificationBadge, ConfidenceIndicator } from "@/components/shared/VerificationPanel";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Progress } from "@/components/ui/progress";

const API = process.env.REACT_APP_BACKEND_URL;

const PROVIDER_TYPES = [
  { value: "chiropractor", label: "Chiropractor" },
  { value: "pain_management", label: "Pain Management" },
  { value: "orthopedic", label: "Orthopedic Surgeon" },
  { value: "neurologist", label: "Neurologist" },
  { value: "physical_therapy", label: "Physical Therapy" },
  { value: "imaging_center", label: "Imaging Center" },
  { value: "surgery_center", label: "Surgery Center" },
  { value: "medical_clinic", label: "Medical Clinic" },
  { value: "dme_supplier", label: "DME Supplier" },
  { value: "other", label: "Other" }
];

const SEVERITY_COLORS = {
  critical: { bg: '#FEE2E2', text: '#991B1B', border: '#FCA5A5' },
  high: { bg: '#FEF3C7', text: '#92400E', border: '#FCD34D' },
  medium: { bg: '#FEF9C3', text: '#854D0E', border: '#FDE047' },
  low: { bg: '#DCFCE7', text: '#166534', border: '#86EFAC' }
};

const MedicalProviderIntel = () => {
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [verificationStatus, setVerificationStatus] = useState("ai_generated");
  const [billingResult, setBillingResult] = useState(null);
  const [networkResult, setNetworkResult] = useState(null);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState("provider");
  
  // Provider Analysis Form
  const [providerName, setProviderName] = useState("");
  const [providerType, setProviderType] = useState("");
  const [location, setLocation] = useState("");
  const [opposingFirm, setOpposingFirm] = useState("");
  const [caseContext, setCaseContext] = useState("");
  
  // Billing Analysis Form
  const [billingProvider, setBillingProvider] = useState("");
  const [billingLocation, setBillingLocation] = useState("");
  const [procedureCodes, setProcedureCodes] = useState("");
  const [billedAmounts, setBilledAmounts] = useState("");
  
  // Network Mapping Form
  const [firmName, setFirmName] = useState("");
  const [firmLocation, setFirmLocation] = useState("");

  const analyzeProvider = async () => {
    if (!providerName.trim()) {
      setError("Please enter a provider name");
      return;
    }
    
    setLoading(true);
    setError(null);
    setResult(null);
    
    try {
      const response = await fetch(`${API}/api/medical-intel/analyze-provider`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          provider_name: providerName.trim(),
          provider_type: providerType || null,
          location: location || null,
          opposing_firm: opposingFirm || null,
          case_context: caseContext || null,
          analysis_types: ["provider_profile", "attorney_network", "billing_analysis"]
        })
      });
      
      if (!response.ok) throw new Error("Analysis failed");
      
      const data = await response.json();
      setResult(data);
    } catch (err) {
      setError("Failed to analyze provider. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const analyzeBilling = async () => {
    if (!billingProvider.trim() || !procedureCodes.trim() || !billedAmounts.trim()) {
      setError("Please enter provider name, CPT codes, and billed amounts");
      return;
    }
    
    setLoading(true);
    setError(null);
    setBillingResult(null);
    
    try {
      const codes = procedureCodes.split(",").map(c => c.trim());
      const amounts = billedAmounts.split(",").map(a => parseFloat(a.trim()));
      
      const response = await fetch(`${API}/api/medical-intel/analyze-billing`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          provider_name: billingProvider.trim(),
          procedure_codes: codes,
          billed_amounts: amounts,
          location: billingLocation || "California"
        })
      });
      
      if (!response.ok) throw new Error("Billing analysis failed");
      
      const data = await response.json();
      setBillingResult(data);
    } catch (err) {
      setError("Failed to analyze billing. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const mapNetwork = async () => {
    if (!firmName.trim()) {
      setError("Please enter a firm name");
      return;
    }
    
    setLoading(true);
    setError(null);
    setNetworkResult(null);
    
    try {
      const response = await fetch(`${API}/api/medical-intel/map-network`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          attorney_or_firm: firmName.trim(),
          location: firmLocation || null,
          practice_area: "personal_injury",
          time_period: "5_years"
        })
      });
      
      if (!response.ok) throw new Error("Network mapping failed");
      
      const data = await response.json();
      setNetworkResult(data);
    } catch (err) {
      setError("Failed to map network. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const resetAll = () => {
    setResult(null);
    setBillingResult(null);
    setNetworkResult(null);
    setError(null);
  };

  return (
    <div className="space-y-6" data-testid="medical-intel">
      {/* Header */}
      <div className="rounded-3xl p-6" style={{ background: 'linear-gradient(135deg, #DC2626, #991B1B)', boxShadow: '0 20px 40px rgba(220,38,38,.25)' }}>
        <div className="flex items-center gap-3 mb-2">
          <Stethoscope className="h-8 w-8 text-white" />
          <h1 className="text-2xl font-bold text-white">Medical Provider Intelligence</h1>
        </div>
        <p className="text-red-100">
          Defense tool for identifying provider-attorney networks, billing inflation, and building case strategy
        </p>
      </div>

      {/* Analysis Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid grid-cols-3 w-full bg-slate-100 p-1 rounded-xl">
          <TabsTrigger value="provider" className="rounded-lg flex items-center gap-2">
            <Search className="h-4 w-4" />
            Provider Analysis
          </TabsTrigger>
          <TabsTrigger value="billing" className="rounded-lg flex items-center gap-2">
            <Receipt className="h-4 w-4" />
            Billing Analysis
          </TabsTrigger>
          <TabsTrigger value="network" className="rounded-lg flex items-center gap-2">
            <Network className="h-4 w-4" />
            Network Mapping
          </TabsTrigger>
        </TabsList>

        {/* Provider Analysis Tab */}
        <TabsContent value="provider" className="mt-6">
          {!result ? (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Stethoscope className="h-5 w-5 text-red-600" />
                  Provider Profile Analysis
                </CardTitle>
                <CardDescription>
                  Investigate medical provider for attorney associations, billing patterns, and red flags
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <Label>Provider Name <span className="text-red-500">*</span></Label>
                    <Input
                      placeholder="Dr. John Smith or ABC Medical Clinic"
                      value={providerName}
                      onChange={(e) => setProviderName(e.target.value)}
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label>Provider Type</Label>
                    <Select value={providerType} onValueChange={setProviderType}>
                      <SelectTrigger className="mt-1">
                        <SelectValue placeholder="Select type..." />
                      </SelectTrigger>
                      <SelectContent>
                        {PROVIDER_TYPES.map(type => (
                          <SelectItem key={type.value} value={type.value}>{type.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Location</Label>
                    <Input
                      placeholder="City, State"
                      value={location}
                      onChange={(e) => setLocation(e.target.value)}
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label>Opposing Plaintiff Firm</Label>
                    <Input
                      placeholder="Morgan & Morgan, etc."
                      value={opposingFirm}
                      onChange={(e) => setOpposingFirm(e.target.value)}
                      className="mt-1"
                    />
                  </div>
                </div>
                <div>
                  <Label>Case Context</Label>
                  <Textarea
                    placeholder="Describe the case, injury type, and specific concerns about this provider..."
                    value={caseContext}
                    onChange={(e) => setCaseContext(e.target.value)}
                    className="mt-1"
                    rows={3}
                  />
                </div>

                {error && (
                  <Alert variant="destructive">
                    <AlertTriangle className="h-4 w-4" />
                    <AlertDescription>{error}</AlertDescription>
                  </Alert>
                )}

                <Button
                  onClick={analyzeProvider}
                  disabled={loading || !providerName.trim()}
                  className="w-full h-12 rounded-xl font-bold text-white"
                  style={{ background: 'linear-gradient(135deg, #DC2626, #991B1B)' }}
                >
                  {loading ? (
                    <>
                      <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                      Analyzing Provider...
                    </>
                  ) : (
                    <>
                      Analyze Provider
                      <ChevronRight className="ml-2 h-5 w-5" />
                    </>
                  )}
                </Button>
              </CardContent>
            </Card>
          ) : (
            /* Provider Results */
            <div className="space-y-6">
              <Card>
                <CardContent className="pt-6">
                  <div className="flex items-start justify-between">
                    <div>
                      <div className="flex items-center gap-3 mb-2">
                        <Stethoscope className="h-6 w-6 text-red-600" />
                        <h2 className="text-xl font-bold text-slate-800">{result.provider_name}</h2>
                        <Badge variant={result.risk_score > 70 ? "destructive" : result.risk_score > 40 ? "warning" : "default"}>
                          Risk Score: {result.risk_score}/100
                        </Badge>
                      </div>
                      {result.provider_profile && (
                        <p className="text-slate-600">
                          {result.provider_profile.provider_type} • {result.provider_profile.practice_locations?.join(", ")}
                        </p>
                      )}
                    </div>
                    <Button variant="outline" onClick={resetAll}>New Analysis</Button>
                  </div>
                </CardContent>
              </Card>

              {/* Risk Score Visualization */}
              <Card className="border-2 border-red-200">
                <CardHeader className="bg-red-50">
                  <CardTitle className="text-lg text-red-800 flex items-center gap-2">
                    <AlertOctagon className="h-5 w-5" />
                    Defense Risk Assessment
                  </CardTitle>
                </CardHeader>
                <CardContent className="pt-4">
                  <div className="mb-4">
                    <div className="flex justify-between text-sm mb-2">
                      <span>Risk Level</span>
                      <span className={`font-bold ${result.risk_score > 70 ? 'text-red-600' : result.risk_score > 40 ? 'text-amber-600' : 'text-green-600'}`}>
                        {result.risk_score > 70 ? 'HIGH RISK' : result.risk_score > 40 ? 'MODERATE RISK' : 'LOW RISK'}
                      </span>
                    </div>
                    <Progress value={result.risk_score} className="h-3" />
                  </div>
                  <p className="text-slate-700">{result.summary}</p>
                </CardContent>
              </Card>

              {/* Red Flags */}
              {result.red_flags && result.red_flags.length > 0 && (
                <Card className="border-2 border-amber-200">
                  <CardHeader className="bg-amber-50">
                    <CardTitle className="text-lg text-amber-800 flex items-center gap-2">
                      <AlertTriangle className="h-5 w-5" />
                      Red Flags Identified ({result.red_flags.length})
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="pt-4 space-y-3">
                    {result.red_flags.map((flag, i) => (
                      <div 
                        key={i} 
                        className="p-4 rounded-xl border-2"
                        style={{
                          background: SEVERITY_COLORS[flag.severity]?.bg || '#F8FAFC',
                          borderColor: SEVERITY_COLORS[flag.severity]?.border || '#E2E8F0'
                        }}
                      >
                        <div className="flex items-start gap-3">
                          <XCircle className="h-5 w-5 mt-0.5 flex-shrink-0" style={{ color: SEVERITY_COLORS[flag.severity]?.text || '#475569' }} />
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-1">
                              <span className="font-semibold" style={{ color: SEVERITY_COLORS[flag.severity]?.text }}>{flag.indicator}</span>
                              <Badge variant="outline" className="text-xs">{flag.severity.toUpperCase()}</Badge>
                            </div>
                            <p className="text-sm text-slate-600 mb-2">{flag.evidence}</p>
                            <p className="text-sm font-medium" style={{ color: SEVERITY_COLORS[flag.severity]?.text }}>
                              Action: {flag.recommendation}
                            </p>
                          </div>
                        </div>
                      </div>
                    ))}
                  </CardContent>
                </Card>
              )}

              {/* Attorney Associations */}
              {result.attorney_associations && result.attorney_associations.length > 0 && (
                <Card>
                  <CardHeader>
                    <CardTitle className="text-lg flex items-center gap-2">
                      <Users className="h-5 w-5 text-blue-600" />
                      Attorney/Firm Associations ({result.attorney_associations.length})
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-3">
                      {result.attorney_associations.map((assoc, i) => (
                        <div key={i} className="p-4 rounded-xl bg-slate-50 border">
                          <div className="flex items-start justify-between">
                            <div>
                              <h4 className="font-bold text-slate-800">{assoc.attorney_or_firm}</h4>
                              <p className="text-sm text-slate-600">{assoc.relationship_type}</p>
                            </div>
                            <div className="text-right">
                              <div className="text-2xl font-bold text-red-600">{assoc.case_count}</div>
                              <div className="text-xs text-slate-500">cases</div>
                            </div>
                          </div>
                          {assoc.total_billings && (
                            <div className="mt-2 text-sm">
                              <span className="text-slate-500">Total Billings:</span>{" "}
                              <span className="font-semibold text-slate-800">{assoc.total_billings}</span>
                            </div>
                          )}
                          {assoc.recent_cases && assoc.recent_cases.length > 0 && (
                            <div className="mt-2">
                              <div className="text-xs text-slate-500 mb-1">Recent Cases:</div>
                              <div className="flex flex-wrap gap-1">
                                {assoc.recent_cases.slice(0, 3).map((c, ci) => (
                                  <Badge key={ci} variant="outline" className="text-xs">{c}</Badge>
                                ))}
                              </div>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Deposition Questions */}
              <Card className="border-2 border-blue-200">
                <CardHeader className="bg-blue-50">
                  <CardTitle className="text-lg text-blue-800 flex items-center gap-2">
                    <HelpCircle className="h-5 w-5" />
                    Suggested Deposition Questions
                  </CardTitle>
                </CardHeader>
                <CardContent className="pt-4">
                  <ol className="space-y-3">
                    {result.deposition_questions?.map((q, i) => (
                      <li key={i} className="flex items-start gap-3">
                        <span className="w-6 h-6 rounded-full bg-blue-100 text-blue-700 flex items-center justify-center text-sm font-bold flex-shrink-0">
                          {i + 1}
                        </span>
                        <span className="text-slate-700">{q}</span>
                      </li>
                    ))}
                  </ol>
                </CardContent>
              </Card>

              {/* Defense Recommendations */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg flex items-center gap-2">
                    <Shield className="h-5 w-5 text-emerald-600" />
                    Defense Strategy Recommendations
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid md:grid-cols-2 gap-3">
                    {result.defense_recommendations?.map((rec, i) => (
                      <div key={i} className="flex items-start gap-3 p-3 rounded-xl bg-emerald-50 border border-emerald-200">
                        <CheckCircle2 className="h-5 w-5 text-emerald-600 flex-shrink-0 mt-0.5" />
                        <span className="text-slate-700">{rec}</span>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </div>
          )}
        </TabsContent>

        {/* Billing Analysis Tab */}
        <TabsContent value="billing" className="mt-6">
          {!billingResult ? (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Receipt className="h-5 w-5 text-emerald-600" />
                  Billing Inflation Analysis
                </CardTitle>
                <CardDescription>
                  Compare provider charges to Medicare rates and industry standards
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <Label>Provider Name <span className="text-red-500">*</span></Label>
                    <Input
                      placeholder="Provider or facility name"
                      value={billingProvider}
                      onChange={(e) => setBillingProvider(e.target.value)}
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label>Location</Label>
                    <Input
                      placeholder="City, State"
                      value={billingLocation}
                      onChange={(e) => setBillingLocation(e.target.value)}
                      className="mt-1"
                    />
                  </div>
                </div>
                <div>
                  <Label>CPT Codes <span className="text-red-500">*</span></Label>
                  <Input
                    placeholder="99213, 72148, 97110 (comma separated)"
                    value={procedureCodes}
                    onChange={(e) => setProcedureCodes(e.target.value)}
                    className="mt-1"
                  />
                  <p className="text-xs text-slate-500 mt-1">Enter CPT codes separated by commas</p>
                </div>
                <div>
                  <Label>Billed Amounts <span className="text-red-500">*</span></Label>
                  <Input
                    placeholder="250, 1500, 175 (comma separated, same order as codes)"
                    value={billedAmounts}
                    onChange={(e) => setBilledAmounts(e.target.value)}
                    className="mt-1"
                  />
                  <p className="text-xs text-slate-500 mt-1">Enter amounts in same order as CPT codes</p>
                </div>

                {error && (
                  <Alert variant="destructive">
                    <AlertTriangle className="h-4 w-4" />
                    <AlertDescription>{error}</AlertDescription>
                  </Alert>
                )}

                <Button
                  onClick={analyzeBilling}
                  disabled={loading}
                  className="w-full h-12 rounded-xl font-bold text-white"
                  style={{ background: 'linear-gradient(135deg, #059669, #047857)' }}
                >
                  {loading ? (
                    <>
                      <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                      Analyzing Billing...
                    </>
                  ) : (
                    <>
                      Analyze Billing Patterns
                      <TrendingUp className="ml-2 h-5 w-5" />
                    </>
                  )}
                </Button>
              </CardContent>
            </Card>
          ) : (
            /* Billing Results */
            <div className="space-y-6">
              <Card>
                <CardContent className="pt-6">
                  <div className="flex items-start justify-between mb-4">
                    <div>
                      <h2 className="text-xl font-bold text-slate-800">{billingResult.provider_name}</h2>
                      <p className="text-slate-600">Billing Inflation Analysis</p>
                    </div>
                    <Button variant="outline" onClick={resetAll}>New Analysis</Button>
                  </div>

                  {/* Totals Summary */}
                  <div className="grid grid-cols-4 gap-4 p-4 rounded-xl bg-red-50 border border-red-200">
                    <div className="text-center">
                      <div className="text-sm text-slate-500">Total Billed</div>
                      <div className="text-2xl font-bold text-slate-800">
                        ${billingResult.totals?.total_billed?.toLocaleString()}
                      </div>
                    </div>
                    <div className="text-center">
                      <div className="text-sm text-slate-500">Medicare Equivalent</div>
                      <div className="text-2xl font-bold text-emerald-600">
                        ${billingResult.totals?.total_medicare_equivalent?.toLocaleString()}
                      </div>
                    </div>
                    <div className="text-center">
                      <div className="text-sm text-slate-500">Inflation</div>
                      <div className="text-2xl font-bold text-red-600">
                        {billingResult.totals?.overall_inflation_percentage}%
                      </div>
                    </div>
                    <div className="text-center">
                      <div className="text-sm text-slate-500">Potential Reduction</div>
                      <div className="text-2xl font-bold text-blue-600">
                        ${billingResult.totals?.potential_reduction?.toLocaleString()}
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Procedure Breakdown */}
              <Card>
                <CardHeader>
                  <CardTitle>Procedure-by-Procedure Analysis</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    {billingResult.billing_analysis?.map((item, i) => (
                      <div key={i} className="p-4 rounded-xl border">
                        <div className="flex items-start justify-between mb-3">
                          <div>
                            <div className="font-bold text-slate-800">{item.procedure_code}</div>
                            <div className="text-sm text-slate-600">{item.procedure_name}</div>
                          </div>
                          <Badge variant={item.inflation_percentage > 300 ? "destructive" : item.inflation_percentage > 150 ? "warning" : "default"}>
                            {item.inflation_percentage}% over Medicare
                          </Badge>
                        </div>
                        <div className="grid grid-cols-4 gap-4 text-sm">
                          <div>
                            <div className="text-slate-500">Billed</div>
                            <div className="font-bold text-red-600">${item.billed_amount?.toLocaleString()}</div>
                          </div>
                          <div>
                            <div className="text-slate-500">Medicare</div>
                            <div className="font-bold text-emerald-600">${item.medicare_rate?.toLocaleString()}</div>
                          </div>
                          <div>
                            <div className="text-slate-500">UCR Avg</div>
                            <div className="font-bold">${item.ucr_average?.toLocaleString()}</div>
                          </div>
                          <div>
                            <div className="text-slate-500">Regional Avg</div>
                            <div className="font-bold">${item.regional_average?.toLocaleString()}</div>
                          </div>
                        </div>
                        <div className="mt-3 p-2 rounded-lg bg-blue-50 text-sm text-blue-800">
                          {item.recommendation}
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>

              {/* Defense Strategy */}
              <Card className="border-2 border-emerald-200">
                <CardHeader className="bg-emerald-50">
                  <CardTitle className="text-lg text-emerald-800">Defense Strategy for Billing Challenge</CardTitle>
                </CardHeader>
                <CardContent className="pt-4">
                  <div className="space-y-2">
                    {billingResult.defense_strategy?.map((strategy, i) => (
                      <div key={i} className="flex items-start gap-3 p-3 rounded-lg bg-white border">
                        <CheckCircle2 className="h-5 w-5 text-emerald-600 flex-shrink-0 mt-0.5" />
                        <span className="text-slate-700">{strategy}</span>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </div>
          )}
        </TabsContent>

        {/* Network Mapping Tab */}
        <TabsContent value="network" className="mt-6">
          {!networkResult ? (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Network className="h-5 w-5 text-purple-600" />
                  Provider-Attorney Network Mapping
                </CardTitle>
                <CardDescription>
                  Map the network of medical providers commonly used by a plaintiff firm
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <Label>Plaintiff Firm Name <span className="text-red-500">*</span></Label>
                    <Input
                      placeholder="Morgan & Morgan, etc."
                      value={firmName}
                      onChange={(e) => setFirmName(e.target.value)}
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label>Location</Label>
                    <Input
                      placeholder="City, State"
                      value={firmLocation}
                      onChange={(e) => setFirmLocation(e.target.value)}
                      className="mt-1"
                    />
                  </div>
                </div>

                {error && (
                  <Alert variant="destructive">
                    <AlertTriangle className="h-4 w-4" />
                    <AlertDescription>{error}</AlertDescription>
                  </Alert>
                )}

                <Button
                  onClick={mapNetwork}
                  disabled={loading || !firmName.trim()}
                  className="w-full h-12 rounded-xl font-bold text-white"
                  style={{ background: 'linear-gradient(135deg, #7C3AED, #5B21B6)' }}
                >
                  {loading ? (
                    <>
                      <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                      Mapping Network...
                    </>
                  ) : (
                    <>
                      Map Provider Network
                      <Network className="ml-2 h-5 w-5" />
                    </>
                  )}
                </Button>
              </CardContent>
            </Card>
          ) : (
            /* Network Results */
            <div className="space-y-6">
              <Card>
                <CardContent className="pt-6">
                  <div className="flex items-start justify-between mb-4">
                    <div>
                      <h2 className="text-xl font-bold text-slate-800">{networkResult.firm_name}</h2>
                      <p className="text-slate-600">Medical Provider Network</p>
                    </div>
                    <Button variant="outline" onClick={resetAll}>New Analysis</Button>
                  </div>

                  {networkResult.network_characteristics && (
                    <div className="grid grid-cols-3 gap-4 p-4 rounded-xl bg-purple-50 border border-purple-200">
                      <div className="text-center">
                        <div className="text-sm text-slate-500">Total Providers</div>
                        <div className="text-2xl font-bold text-purple-700">
                          {networkResult.network_characteristics.total_providers}
                        </div>
                      </div>
                      <div className="text-center">
                        <div className="text-sm text-slate-500">Primary Referrals</div>
                        <div className="text-2xl font-bold text-purple-700">
                          {networkResult.network_characteristics.primary_referral_sources}
                        </div>
                      </div>
                      <div className="text-center">
                        <div className="text-sm text-slate-500">Est. Annual Billing</div>
                        <div className="text-2xl font-bold text-purple-700">
                          {networkResult.network_characteristics.estimated_annual_billing}
                        </div>
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Network Providers */}
              <Card>
                <CardHeader>
                  <CardTitle>Identified Network Providers</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {networkResult.network_providers?.map((provider, i) => (
                      <div key={i} className="p-4 rounded-xl border">
                        <div className="flex items-start justify-between mb-2">
                          <div>
                            <div className="font-bold text-slate-800">{provider.provider_name}</div>
                            <div className="text-sm text-slate-600">{provider.provider_type}</div>
                          </div>
                          <Badge variant={provider.relationship_strength === "primary" ? "destructive" : "default"}>
                            {provider.relationship_strength}
                          </Badge>
                        </div>
                        <div className="text-sm text-slate-600 mb-2">
                          <span className="font-medium">Volume:</span> {provider.estimated_case_volume}
                        </div>
                        {provider.red_flags && provider.red_flags.length > 0 && (
                          <div className="flex flex-wrap gap-1 mt-2">
                            {provider.red_flags.map((flag, fi) => (
                              <Badge key={fi} variant="outline" className="text-xs text-red-600 border-red-300">
                                {flag}
                              </Badge>
                            ))}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>

              {/* Defense Opportunities */}
              <Card className="border-2 border-emerald-200">
                <CardHeader className="bg-emerald-50">
                  <CardTitle className="text-lg text-emerald-800">Defense Opportunities</CardTitle>
                </CardHeader>
                <CardContent className="pt-4">
                  <div className="space-y-2">
                    {networkResult.defense_opportunities?.map((opp, i) => (
                      <div key={i} className="flex items-start gap-3 p-3 rounded-lg bg-white border">
                        <CheckCircle2 className="h-5 w-5 text-emerald-600 flex-shrink-0 mt-0.5" />
                        <span className="text-slate-700">{opp}</span>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </div>
          )}
        </TabsContent>
      </Tabs>
      
      {/* Lawyer Verification */}
      {result && (
        <div className="mt-6 max-w-4xl mx-auto">
          <VerificationPanel
            itemId={result.analysis_id || result.id || "medical-intel"}
            itemType="analysis"
            currentStatus={verificationStatus}
            onStatusChange={setVerificationStatus}
          />
        </div>
      )}
    </div>
  );
};

export default MedicalProviderIntel;
